import { z } from "zod";

export enum MatchMode {
  MATCH_MODE_UNSPECIFIED = "MATCH_MODE_UNSPECIFIED",
  ALL = "ALL",
  ANY = "ANY",
}

export enum DeviceType {
  DEVICE_TYPE_UNSPECIFIED = "DEVICE_TYPE_UNSPECIFIED",
  DEVICE_TYPE_DESKTOP = "DEVICE_TYPE_DESKTOP",
  DEVICE_TYPE_MOBILE = "DEVICE_TYPE_MOBILE",
  DEVICE_TYPE_TABLET = "DEVICE_TYPE_TABLET",
}

export enum DayOfWeek {
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}

export enum SeasonType {
  SEASON_TYPE_UNSPECIFIED = "SEASON_TYPE_UNSPECIFIED",
  SPRING = "SPRING",
  SUMMER = "SUMMER",
  AUTUMN = "AUTUMN",
  WINTER = "WINTER",
}

export enum Hemisphere {
  HEMISPHERE_UNSPECIFIED = "HEMISPHERE_UNSPECIFIED",
  NORTHERN = "NORTHERN",
  SOUTHERN = "SOUTHERN",
}

export enum ConditionType {
  OCCUPANCY = "occupancy",
  CHECK_IN_DAY = "checkInDay",
  CHECK_OUT_DAY = "checkOutDay",
  LENGTH_OF_STAY = "lengthOfStay",
  BOOKING_WINDOW = "bookingWindow",
  TRAVEL_WINDOW = "travelWindow",
  NUMBER_OF_ROOMS = "numberOfRooms",
  DEVICE = "device",
  REFERRER = "referrer",
  UTM_SOURCE = "utmSource",
  ORIGIN = "origin",
  SEASON = "season",
}

export const OccupancyConditionSchema = z.object({
  values: z.array(z.string()),
});

export const DaysOnlyConditionSchema = z.object({
  activeDays: z.array(z.nativeEnum(DayOfWeek)).optional(),
});

export const DateRangeWindowSchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  activeDays: z.array(z.nativeEnum(DayOfWeek)).optional(),
  startHour: z.string().optional(),
  endHour: z.string().optional(),
  timeZone: z.string().optional(),
});

export const MinMaxConditionSchema = z.object({
  min: z.number(),
  max: z.number().optional().nullable(),
});

export const DeviceConditionSchema = z.object({
  types: z.array(z.nativeEnum(DeviceType)),
});

export const StringListConditionSchema = z.object({
  values: z.array(z.string()),
});

export const SeasonConditionSchema = z.object({
  seasons: z.array(z.nativeEnum(SeasonType)),
  hemisphere: z.nativeEnum(Hemisphere).default(Hemisphere.NORTHERN),
});

export const OrchestrationConditionSchema = z.object({
  occupancy: OccupancyConditionSchema.optional(),
  checkInDay: DaysOnlyConditionSchema.optional(),
  checkOutDay: DaysOnlyConditionSchema.optional(),
  lengthOfStay: MinMaxConditionSchema.optional(),
  bookingWindow: z.array(DateRangeWindowSchema).optional(),
  travelWindow: z.array(DateRangeWindowSchema).optional(),
  numberOfRooms: MinMaxConditionSchema.optional(),
  device: DeviceConditionSchema.optional(),
  referrer: StringListConditionSchema.optional(),
  utmSource: StringListConditionSchema.optional(),
  origin: StringListConditionSchema.optional(),
  season: SeasonConditionSchema.optional(),
});

export const OrchestrationCriteriaSchema = z
  .object({
    matchMode: z.nativeEnum(MatchMode).default(MatchMode.ALL),
    conditions: z.array(OrchestrationConditionSchema).default([]),
  })
  // Hardening (US-014): mirrors the backend's `OrchestrationCriteria.kt` constructor guard
  // (`require(types.size == types.distinct().size)`) — at most one condition per `ConditionType`.
  // `scenarioComparability.ts`'s `groupByType`/`compareScenarioCriteria` normalization logic
  // depends on this invariant holding for any input that has gone through `.parse()`.
  .superRefine((value, ctx) => {
    const seen = new Set<ConditionType>();
    value.conditions.forEach((condition, index) => {
      const type = getConditionType(condition);
      if (type === null) return;
      if (seen.has(type)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["conditions", index],
          message: `Duplicate condition type "${type}" — only one condition per type is allowed.`,
        });
      }
      seen.add(type);
    });
  });

export type OrchestrationCondition = z.infer<typeof OrchestrationConditionSchema>;
export type OrchestrationCriteria = z.infer<typeof OrchestrationCriteriaSchema>;

export function getConditionType(condition: OrchestrationCondition): ConditionType | null {
  if (condition.occupancy) return ConditionType.OCCUPANCY;
  if (condition.checkInDay) return ConditionType.CHECK_IN_DAY;
  if (condition.checkOutDay) return ConditionType.CHECK_OUT_DAY;
  if (condition.lengthOfStay) return ConditionType.LENGTH_OF_STAY;
  if (condition.bookingWindow) return ConditionType.BOOKING_WINDOW;
  if (condition.travelWindow) return ConditionType.TRAVEL_WINDOW;
  if (condition.numberOfRooms) return ConditionType.NUMBER_OF_ROOMS;
  if (condition.device) return ConditionType.DEVICE;
  if (condition.referrer) return ConditionType.REFERRER;
  if (condition.utmSource) return ConditionType.UTM_SOURCE;
  if (condition.origin) return ConditionType.ORIGIN;
  if (condition.season) return ConditionType.SEASON;
  return null;
}
